import React from "react";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import {
  COVERAGE,
  COUNTRY_OPTIONS,
  DEGREE_LEVELS,
  FIELD_OF_STUDY,
  MODE_OF_STUDY,
  countryLabel,
  labelize,
} from "../../helpers/advisorVocab";

const inputClass =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white";

const labelClass =
  "block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

function MultiSelect({ options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((x) => x !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-600">
      {options.map((opt) => {
        const on = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`text-xs px-2 py-1 rounded border ${
              on
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-200"
            }`}
          >
            {labelize(opt)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Structured field editor for school | scholarship | programme.
 * @param {{ category: string, value: object, onChange: Function, onExtract: Function, extractLoading?: boolean, extractDisabled?: boolean, completenessScore?: number|null }} props
 */
const StructuredFieldsPanel = ({
  category,
  value,
  onChange,
  onExtract,
  extractLoading = false,
  extractDisabled = false,
  completenessScore = null,
}) => {
  if (!["school", "scholarship", "programme"].includes(category)) {
    return null;
  }

  const set = (patch) => onChange({ ...value, ...patch });
  const setNested = (key, patch) =>
    onChange({ ...value, [key]: { ...(value[key] || {}), ...patch } });
  const countryCode = value.country ?
    String(value.country).toUpperCase() :
    "";
  const knownCountry = COUNTRY_OPTIONS.some((o) => o.code === countryCode);
  const countrySelectValue = knownCountry ?
    countryCode :
    (value.country || "");

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-left dark:border-gray-600 dark:bg-gray-700/40">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Structured fields (AI Match)
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Apps render the HTML body. The advisor uses these fields. Auto-extract
            from the body, then edit anything that looks wrong.
          </p>
          {completenessScore != null && (
            <p className="text-xs text-gray-500 mt-0.5">
              Completeness: {Math.round(Number(completenessScore) * 100)}%
            </p>
          )}
        </div>
        <Button
          color="purple"
          size="xs"
          type="button"
          onClick={onExtract}
          disabled={extractDisabled || extractLoading}
          isProcessing={extractLoading}
          processingSpinner={
            <AiOutlineLoading className="h-4 w-4 animate-spin" />
          }
        >
          Auto-extract with AI
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Country">
          <select
            className={inputClass}
            value={countrySelectValue}
            onChange={(e) => set({ country: e.target.value })}
          >
            <option value="">—</option>
            {value.country &&
              !COUNTRY_OPTIONS.some(
                (o) => o.code === String(value.country).toUpperCase(),
              ) && (
                <option value={value.country}>
                  {countryLabel(value.country)}
                </option>
              )}
            {COUNTRY_OPTIONS.map(({ code, name }) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </Field>

        {category === "school" && (
          <>
            <Field label="Location">
              <input
                className={inputClass}
                value={value.schoolStructured?.location || ""}
                onChange={(e) =>
                  setNested("schoolStructured", { location: e.target.value })
                }
              />
            </Field>
            <Field label="University type">
              <input
                className={inputClass}
                value={value.schoolStructured?.universityType || ""}
                onChange={(e) =>
                  setNested("schoolStructured", {
                    universityType: e.target.value,
                  })
                }
              />
            </Field>
            <Field label="Founded">
              <input
                className={inputClass}
                value={value.schoolStructured?.founded || ""}
                onChange={(e) =>
                  setNested("schoolStructured", { founded: e.target.value })
                }
              />
            </Field>
            <Field label="Enrollment">
              <input
                className={inputClass}
                value={value.schoolStructured?.enrollment || ""}
                onChange={(e) =>
                  setNested("schoolStructured", { enrollment: e.target.value })
                }
              />
            </Field>
            <Field label="Acceptance rate">
              <input
                className={inputClass}
                value={value.schoolStructured?.acceptanceRate || ""}
                onChange={(e) =>
                  setNested("schoolStructured", {
                    acceptanceRate: e.target.value,
                  })
                }
              />
            </Field>
            <Field label="Ranking">
              <input
                className={inputClass}
                value={value.schoolStructured?.ranking || ""}
                onChange={(e) =>
                  setNested("schoolStructured", { ranking: e.target.value })
                }
              />
            </Field>
            <Field label="Website">
              <input
                className={inputClass}
                value={value.schoolStructured?.website || ""}
                onChange={(e) =>
                  setNested("schoolStructured", { website: e.target.value })
                }
              />
            </Field>
          </>
        )}

        {category === "scholarship" && (
          <>
            <Field label="Coverage">
              <select
                className={inputClass}
                value={value.coverage || ""}
                onChange={(e) => set({ coverage: e.target.value })}
              >
                <option value="">—</option>
                {COVERAGE.map((c) => (
                  <option key={c} value={c}>
                    {labelize(c)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Eligible levels">
                <MultiSelect
                  options={DEGREE_LEVELS}
                  value={value.levels}
                  onChange={(levels) => set({ levels })}
                />
              </Field>
            </div>
            <Field label="Amount (raw)">
              <input
                className={inputClass}
                value={value.amountRaw || ""}
                onChange={(e) => set({ amountRaw: e.target.value })}
              />
            </Field>
            <Field label="Currency">
              <input
                className={inputClass}
                value={value.amountCurrency || ""}
                onChange={(e) => set({ amountCurrency: e.target.value })}
                placeholder="EUR"
              />
            </Field>
            <Field label="Amount min USD">
              <input
                type="number"
                className={inputClass}
                value={value.amountMinUSD || ""}
                onChange={(e) => set({ amountMinUSD: e.target.value })}
              />
            </Field>
            <Field label="Amount max USD">
              <input
                type="number"
                className={inputClass}
                value={value.amountMaxUSD || ""}
                onChange={(e) => set({ amountMaxUSD: e.target.value })}
              />
            </Field>
            <Field label="Deadline">
              <input
                type="date"
                className={inputClass}
                value={value.deadlineAt || ""}
                disabled={Boolean(value.isRolling)}
                onChange={(e) => set({ deadlineAt: e.target.value })}
              />
            </Field>
            <Field label="Deadline (raw text)">
              <input
                className={inputClass}
                value={value.deadlineRaw || ""}
                onChange={(e) => set({ deadlineRaw: e.target.value })}
              />
            </Field>
            <div className="flex items-center gap-4 pt-5">
              <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={Boolean(value.isRolling)}
                  onChange={(e) => set({ isRolling: e.target.checked })}
                />
                Rolling deadline
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={value.renewable === true}
                  onChange={(e) => set({ renewable: e.target.checked })}
                />
                Renewable
              </label>
            </div>
            <Field label="Application URL">
              <input
                className={inputClass}
                value={value.applicationUrl || ""}
                onChange={(e) => set({ applicationUrl: e.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Eligibility">
                <textarea
                  rows={3}
                  className={inputClass}
                  value={value.eligibilityText || ""}
                  onChange={(e) => set({ eligibilityText: e.target.value })}
                />
              </Field>
            </div>
          </>
        )}

        {category === "programme" && (
          <>
            <Field label="Degree level">
              <select
                className={inputClass}
                value={value.degreeLevel || ""}
                onChange={(e) => set({ degreeLevel: e.target.value })}
              >
                <option value="">—</option>
                {DEGREE_LEVELS.map((d) => (
                  <option key={d} value={d}>
                    {labelize(d)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Mode of study">
              <select
                className={inputClass}
                value={value.modeOfStudy || ""}
                onChange={(e) => set({ modeOfStudy: e.target.value })}
              >
                <option value="">—</option>
                {MODE_OF_STUDY.map((m) => (
                  <option key={m} value={m}>
                    {labelize(m)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Field of study">
                <MultiSelect
                  options={FIELD_OF_STUDY}
                  value={value.fieldOfStudy}
                  onChange={(fieldOfStudy) => set({ fieldOfStudy })}
                />
              </Field>
            </div>
            <Field label="Tuition (raw)">
              <input
                className={inputClass}
                value={value.tuitionRaw || ""}
                onChange={(e) => set({ tuitionRaw: e.target.value })}
              />
            </Field>
            <Field label="Tuition currency">
              <input
                className={inputClass}
                value={value.tuitionCurrency || ""}
                onChange={(e) => set({ tuitionCurrency: e.target.value })}
              />
            </Field>
            <Field label="Tuition min USD">
              <input
                type="number"
                className={inputClass}
                value={value.tuitionMinUSD || ""}
                onChange={(e) => set({ tuitionMinUSD: e.target.value })}
              />
            </Field>
            <Field label="Tuition max USD">
              <input
                type="number"
                className={inputClass}
                value={value.tuitionMaxUSD || ""}
                onChange={(e) => set({ tuitionMaxUSD: e.target.value })}
              />
            </Field>
            <Field label="Duration (months)">
              <input
                type="number"
                className={inputClass}
                value={value.durationMonths || ""}
                onChange={(e) => set({ durationMonths: e.target.value })}
              />
            </Field>
            <Field label="Language">
              <input
                className={inputClass}
                value={value.languageOfInstruction || ""}
                onChange={(e) =>
                  set({ languageOfInstruction: e.target.value })
                }
              />
            </Field>
            <Field label="Department">
              <input
                className={inputClass}
                value={value.programmeStructured?.department || ""}
                onChange={(e) =>
                  setNested("programmeStructured", {
                    department: e.target.value,
                  })
                }
              />
            </Field>
            <Field label="Faculty">
              <input
                className={inputClass}
                value={value.programmeStructured?.faculty || ""}
                onChange={(e) =>
                  setNested("programmeStructured", { faculty: e.target.value })
                }
              />
            </Field>
            <Field label="ECTS">
              <input
                className={inputClass}
                value={value.programmeStructured?.ects || ""}
                onChange={(e) =>
                  setNested("programmeStructured", { ects: e.target.value })
                }
              />
            </Field>
            <Field label="GPA min">
              <input
                type="number"
                step="0.1"
                className={inputClass}
                value={value.admission?.gpaMin || ""}
                onChange={(e) =>
                  setNested("admission", { gpaMin: e.target.value })
                }
              />
            </Field>
            <Field label="IELTS">
              <input
                type="number"
                step="0.5"
                className={inputClass}
                value={value.admission?.ielts || ""}
                onChange={(e) =>
                  setNested("admission", { ielts: e.target.value })
                }
              />
            </Field>
            <Field label="TOEFL">
              <input
                type="number"
                className={inputClass}
                value={value.admission?.toefl || ""}
                onChange={(e) =>
                  setNested("admission", { toefl: e.target.value })
                }
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Admission notes">
                <textarea
                  rows={2}
                  className={inputClass}
                  value={value.admission?.otherText || ""}
                  onChange={(e) =>
                    setNested("admission", { otherText: e.target.value })
                  }
                />
              </Field>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StructuredFieldsPanel;
